/**
 * Load more / infinite scroll for the collection grid (O My Bag AB test).
 *
 * Markup contract (rendered by sections/collection-grid.liquid when the
 * `pagination_mode` section setting is `load_more` or `infinite` and the
 * collection has a next page):
 *
 *   <div data-load-more-wrapper data-mode="load_more|infinite"
 *        data-next-url="{{ paginate.next.url }}" data-pages="{{ paginate.pages }}">
 *     <button data-load-more-trigger>…</button>          // hidden in infinite mode
 *     <div data-load-more-spinner hidden></div>
 *     <div data-load-more-sentinel></div>               // infinite mode only
 *   </div>
 *
 * Behaviour:
 *  - Clicking the trigger (or reaching the sentinel in infinite mode) fetches
 *    the next page of the current filtered/sorted result set via Shopify's
 *    `?section_id=` section rendering, appends the returned product cards to
 *    the grid and keeps `page=N` in the URL in sync with history.replaceState.
 *  - When the fetched page has no further next page, the trigger is hidden and
 *    the sentinel is no longer observed.
 *  - Filter/sort changes re-render the grid section through
 *    assets/component-collection-filters.js; that script calls
 *    `window.collectionLoadMore.reinit()` afterwards so the fresh fragment's
 *    load-more state is picked up and any in-flight load is aborted.
 */
class CollectionLoadMore {
  constructor() {
    this.states = new Map(); // wrapper element -> state
    this.observer = null;
    this._clickBound = false;
    this.init = this.init.bind(this);
    this.reinit = this.reinit.bind(this);
    this.handleIntersection = this.handleIntersection.bind(this);
  }

  init() {
    // One delegated click listener — survives grid re-renders (filter changes).
    if (!this._clickBound) {
      document.addEventListener("click", (event) => {
        const trigger = event.target.closest("[data-load-more-trigger]");
        if (!trigger) return;
        const wrapper = trigger.closest("[data-load-more-wrapper]");
        if (wrapper) this.loadNextPage(wrapper);
      });
      this._clickBound = true;
    }

    if (!this.observer && "IntersectionObserver" in window) {
      this.observer = new IntersectionObserver(this.handleIntersection, {
        // Trigger roughly 1000px before the sentinel actually reaches the
        // viewport bottom so the next page is usually ready as the user scrolls.
        rootMargin: "0px 0px 1000px 0px",
        threshold: 0,
      });
    }

    this.reinit();
  }

  /**
   * (Re)wire every load-more wrapper currently in the DOM. Called on initial
   * load and again by component-collection-filters.js after a filter/sort
   * change swaps in fresh section HTML.
   */
  reinit() {
    // Abort any load still in flight from the previous render.
    this.states.forEach((state) => {
      if (state.controller) state.controller.abort();
    });
    this.states.clear();

    if (this.observer) this.observer.disconnect();

    document.querySelectorAll("[data-load-more-wrapper]").forEach((wrapper) => {
      const mode = wrapper.dataset.mode;
      if (mode !== "load_more" && mode !== "infinite") return;

      const state = {
        wrapper,
        mode,
        nextUrl: wrapper.dataset.nextUrl || "",
        loading: false,
        controller: null,
      };
      this.states.set(wrapper, state);
      this.observeWrapper(wrapper, state);
    });
  }

  observeWrapper(wrapper, state) {
    if (state.mode !== "infinite") return;

    const trigger = wrapper.querySelector("[data-load-more-trigger]");
    const sentinel = wrapper.querySelector("[data-load-more-sentinel]");

    if (!this.observer) {
      // No IntersectionObserver support: surface the hidden button as a
      // manual fallback so users can still page through the result set.
      if (trigger) trigger.hidden = false;
    } else if (sentinel && state.nextUrl) {
      this.observer.observe(sentinel);
    }
  }

  handleIntersection(entries) {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      const wrapper = entry.target.closest("[data-load-more-wrapper]");
      if (wrapper) this.loadNextPage(wrapper);
    });
  }

  async loadNextPage(wrapper) {
    const state = this.states.get(wrapper);
    if (!state || state.loading || !state.nextUrl) return;

    if (!("fetch" in window)) {
      // Very old browsers: fall back to a plain navigation to the next page.
      window.location.href = state.nextUrl;
      return;
    }

    const section = wrapper.closest("[data-section-collection-grid]");
    const grid = section ? section.querySelector("#product-grid") : null;
    if (!grid) return;

    state.loading = true;
    const controller = new AbortController();
    state.controller = controller;

    grid.setAttribute("aria-busy", "true");
    this.setLoadingUI(wrapper, true);

    const sectionId = grid.dataset.id || "";
    const separator = state.nextUrl.includes("?") ? "&" : "?";
    // `page=N` (plus any filter/sort params already carried by paginate.next.url)
    const url = `${state.nextUrl}${separator}section_id=${encodeURIComponent(sectionId)}`;
    const loadedPage = new URL(state.nextUrl, window.location.origin).searchParams.get("page");

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Load more request failed (${response.status})`);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, "text/html");
      const nextGrid = doc.querySelector("[data-section-collection-grid] #product-grid");

      if (nextGrid && nextGrid.children.length) {
        grid.insertAdjacentHTML("beforeend", nextGrid.innerHTML);
      }

      // The fetched page's own fragment carries the URL for the page after it
      // (or no wrapper at all when we just loaded the last page).
      const nextWrapper = doc.querySelector(
        "[data-section-collection-grid] [data-load-more-wrapper]"
      );
      state.nextUrl = nextWrapper ? nextWrapper.dataset.nextUrl || "" : "";

      // The URL is intentionally left untouched (no history.replaceState) so
      // back/forward and refresh always return to the original landing page
      // rather than a stranded, partially-loaded page=N. Instead, publish a
      // Shopify custom event so a customer-events subscriber (see Custom
      // events in the theme) can tell AJAX-loaded pages apart from standard
      // full-page pagination.
      if (loadedPage && window.Shopify && window.Shopify.analytics) {
        window.Shopify.analytics.publish("collection_load_more", {
          loadedVia: "ajax",
          page: loadedPage,
        });
      }

      // Initialise swipers found in the freshly appended cards.
      // FacetFiltersForm is a top-level class binding (global lexical scope),
      // so it is not exposed on window — guard with typeof instead.
      if (typeof FacetFiltersForm !== "undefined" && FacetFiltersForm.reinitializeSwipers) {
        FacetFiltersForm.reinitializeSwipers();
      }

      if (!state.nextUrl) {
        this.finish(wrapper, state);
      }
    } catch (error) {
      if (error.name === "AbortError") return; // aborted by reinit (filter change)
      // Keep the trigger available so the load can be retried.
      console.warn("[collection load-more] failed to load the next page:", error);
    } finally {
      state.loading = false;
      state.controller = null;
      grid.removeAttribute("aria-busy");
      this.setLoadingUI(wrapper, false);
    }
  }

  finish(wrapper, state) {
    const trigger = wrapper.querySelector("[data-load-more-trigger]");
    const sentinel = wrapper.querySelector("[data-load-more-sentinel]");
    if (trigger) trigger.hidden = true;
    if (sentinel && this.observer) this.observer.unobserve(sentinel);
    wrapper.setAttribute("data-complete", "");
  }

  setLoadingUI(wrapper, loading) {
    const trigger = wrapper.querySelector("[data-load-more-trigger]");
    const spinner = wrapper.querySelector("[data-load-more-spinner]");
    if (trigger) trigger.disabled = loading;
    if (spinner) spinner.hidden = !loading;
  }
}

const collectionLoadMore = new CollectionLoadMore();
window.collectionLoadMore = collectionLoadMore;

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => collectionLoadMore.init());
} else {
  collectionLoadMore.init();
}